/*
 * `import type`, so this is erased at compile time and creates no runtime dependency on the module
 * that defines it - which is the whole point of this file existing separately.
 */
import type {XeroLineConfig} from './ExportInvoiceModal.xeroLines';

/**
 * The Xero line configuration already read, per organisation.
 *
 * Its own module, deliberately. `ExportInvoiceModal.xeroLines` imports `FeatureUtil`, which pulls
 * `ModuleUtil` -> `util` -> canvas-scheduling -> the generic modal conductor -> chart.js; anything
 * that needs only to CLEAR this cache would drag that whole chain in with it. The Xero
 * configuration admin page needs exactly that and nothing else, and importing the big module there
 * broke its test suite before this split.
 *
 * Module-level rather than component state because the point is to survive the modal closing: the
 * whole reason a load is visible at all is that nothing has been read by the time the user clicks
 * Export. A second open, or an open after `prefetchXeroLineConfig`, renders populated.
 *
 * Keyed on tenant, never on client - two clients in the same Xero organisation share one set of
 * accounts, and keying on the client would read the same three endpoints again for each.
 */
export const CONFIG_CACHE = new Map<string, XeroLineConfig>();

/** The shared `xero/contacts` read - see `readXeroContactsOnce`. Module state, like the cache. */
let contactsRead: Promise<{ContactID?: string; TenantId?: string}[] | null> | null = null;

export const getContactsRead = (): Promise<{ContactID?: string; TenantId?: string}[] | null> | null => contactsRead;

export const setContactsRead = (read: Promise<{ContactID?: string; TenantId?: string}[] | null>): void => {
	contactsRead = read;
};

/**
 * Empties both, so the next read goes to the server.
 *
 * Two callers, for two different reasons: tests, where the cache outlives a test file's modules and
 * one test's tenant is another's; and the Xero configuration import page, where an admin who
 * imports products and then opens an invoice in the same SPA session would otherwise see the list
 * as it was before the import.
 */
export const clearXeroLineConfigCache = (): void => {
	CONFIG_CACHE.clear();
	contactsRead = null;
};
